import React, {useState} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useBalance } from '../../Data/Balance';
import { usePlans, descriptions, hidden, unreleased } from '../../Data/Plans';
import { PageLoading } from '../../Components/Loading';
import { classNames } from '../../Services/helpers';
import Icon from '../../Components/Icons/Icon';
import Money from '../../Components/Formatting/Money';
import Button from '../../Components/Button/Button';
import AddCard from './AddCard';
import DateTime from '../../Components/Formatting/DateTime';
import { useDialogues } from '../../Components/Dialogues';
import Carousel, { Item } from '../../Components/Containers/Carousel/Carousel';

const Plan = () => {
    const {plans} = usePlans();
    const {balance, paymentMethod, changePlan, refresh} = useBalance(true);
    const [billing, setBilling] = useState('Yearly');
    const [addingCard, setAddingCard] = useState(false);
    const [requestedPlan, setRequestedPlan] = useState(null);
    const {confirm} = useDialogues();

    const selectPlan = (plan, justAddCard = false) => {
        setRequestedPlan(plan);
        if (!paymentMethod && plan.monthly > 0 && !justAddCard) {
            setAddingCard(true);
            return;
        }

        const currentPlan = plans.find(p => p.name === balance.plan);
        const anniversary = <DateTime date={balance.anniversary} hideTime />;
        const period = billing === 'Yearly' ? 'annually' : 'monthly';
        const amount = <Money pence={billing === 'Yearly' ? plan.yearly : plan.monthly} />;

        let message = null;

        // Changing the billing period of the current plan
        if (plan.id === currentPlan.id) {
            message = <>
                Are you sure you want to change your plan to {plan.name}, billed {period} for {amount}?<br /><br />
                You're already on this plan. We'll start charging you {amount} {period} on {anniversary}.
            </>;

        // Changing plan from free to paid
        } else if (plan.yearly > 0 && currentPlan.yearly === 0) {
            message = <>
                Are you sure you want to change your plan to {plan.name}, billed {period} for {amount}?<br /><br />
                Your plan will be changed right away.
            </>;

        // Changing plan while they're active
        } else if (plan.yearly > 0) {
            message = <>
                Are you sure you want to change your plan to {plan.name}, billed {period} for {amount}?<br /><br />
                Your plan will be changed right away. Leftover credit from your current plan will apply to the new plan, and any extra will be refunded.
            </>;

            // Switching to a free plan
        } else if (currentPlan.yearly > 0 && plan.yearly === 0) {
            message = <>
                Are you sure you want to change to the free plan?<br /><br />
                Your subscription will continue until {anniversary} then automatically downgraded to the free plan. You won't be charged again.
            </>;
        }

        confirm(
            {
                theme: 'primary',
                title: 'Confirm plan change',
                message: message,
            },
            () => changePlan(plan.name, billing)
        )
    }

    const onCardAdded = async () => {
        const p = requestedPlan;
        setAddingCard(false);
        await refresh();
        setTimeout(() => {
            selectPlan(p, true);
        }, 200);
    }

    return <>
        <TopBar left="/billing" title="Plan" />
        <Page>
            {plans && balance ? <Inner {...{plans, balance, billing, setBilling, selectPlan}} /> : <PageLoading />}
        </Page>

        <AddCard isOpen={addingCard}
            setIsOpen={() => {
                setAddingCard(false);
                // Make sure they can request the plan again
                setTimeout(() => setRequestedPlan(null), 500);
            }}
            onComplete={onCardAdded}
        />
    </>
}

const Inner = ({plans, balance, billing, setBilling, selectPlan}) => {
    return <div className="mb-4">
        <SubscriptionPeriod {...{billing, setBilling}} />
        <Carousel className="my-5">
            <Item>
                <ComparePlan {...{balance, billing, selectPlan}}
                    plan={plans.find(p => p.id === 'PRO')}
                    compareTo={plans.find(p => p.id === 'ADVANCED')}
                />
            </Item>
            <Item>
                <ComparePlan {...{balance, billing, selectPlan}}
                    plan={plans.find(p => p.id === 'ADVANCED')}
                    compareTo={plans.find(p => p.id === 'STARTER')}
                />
            </Item>
            <Item>
                <ComparePlan {...{balance, billing, selectPlan}}
                    plan={plans.find(p => p.id === 'STARTER')}
                    compareTo={null}
                />
            </Item>
        </Carousel>

        <FreePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'FREE')} />
        {/*
        <div className="flex flex-col md:flex-row flex-wrap space-4">
            <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'PRO')} />
            <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'STANDARD')} />
            <ComparePlan {...{balance, billing, selectPlan}} plan={plans.find(p => p.id === 'FREE')} />
        </div>
        */}
    </div>
}

const ComparePlan = ({plan, compareTo, balance, billing, selectPlan}) => {
    return <div className=""> {/* md:w-[45%] w-full mx-auto my-4 p-4 border rounded-xl flex flex-col space-y-4 */}
        <div className="flex flex-row justify-between items-start">
            <h3 className="font-bold text-brand text-4xl">
                {plan.name}
                {plan.name === balance.plan && <Icon icon="CircleCheck" size="2x" className="ml-2 fill-brand" />}
            </h3>
            <Price plan={plan} billing={billing} />
        </div>
        <p className="my-2 text-sm text-muted text-balance">
            {descriptions[plan.id]}
        </p>

        <Button className="w-full"
            theme="primary"
            label={
                balance.plan === plan.name ?
                (billing === balance.subscriptionPeriod ? 'Current plan' : `Go ${billing}`) :
                'Select'
            }
            disabled={balance.plan === plan.name && billing === balance.subscriptionPeriod}
            onClick={() => selectPlan(plan)}
        />

        <ul className="flex flex-col space-y-2">

            {compareTo && <li key="plus" className="text-muted fill-brand">
                <Icon icon="Check" className="mr-2" theme="solid" /> Everything in {compareTo.name}, plus...
            </li>}

            {Object.entries(plan.features).sort(([, a], [, b]) => a.name.localeCompare(b.name)).map(([key]) => {
                if (hidden.includes(key) || unreleased.includes(key)) {
                    return null;
                }

                const {name, enabled} = plan.features[key];
                const {enabled: enabledInCompare} = compareTo?.features[key] ?? {enabled: false};

                if (!enabled || enabledInCompare) {
                    return null;
                }

                return <li key={key} className="text-slate-900 font-semibold">
                    <Icon icon="Check" className="mr-2 fill-brand" theme="solid" inline />
                    {name}
                </li>
            })}
        </ul>


    </div>
}

const FreePlan = ({balance, billing, selectPlan, plan}) => {
    const features = Object.entries(plan.features)
                            .sort(([, a], [, b]) => a.name.localeCompare(b.name))
                            .filter(([key]) => !hidden.includes(key) && !unreleased.includes(key) && plan.features[key].enabled)
                            .map(([key]) => plan.features[key].name)
                            .join(', ');

    return <div className="my-4">
        <h3 className="font-bold text-brand text-xl mb-2">
            Free plan
        </h3>
        <p className="text-muted">
            We also offer a limited free plan which includes:
            <br />
            {features}.<br />
            You can't send customers message or notifications in the free plan.
        </p>
        <Button theme="primary"
            label={balance.plan === plan.name ? 'Current plan' : 'Downgrade'}
            onClick={() => selectPlan(plan)}
            disabled={balance.plan === plan.name}
        />
    </div>
}

const Price = ({plan, billing}) => {
    let big, small;
    if (plan.yearly === 0) {
        big = 'Free forever'
        small = <>&nbsp;</>;
    } else if (billing === 'Yearly') {
        big = <><Money pence={plan.yearly/12} />/m</>
        small = <>Billed <Money pence={plan.yearly} />/y</>
    } else {
        big = <><Money pence={plan.monthly} />/m</>
        small = <>Equiv. <Money pence={plan.monthly*12} />/y</>
    }

    return <div className="text-right text-nowrap">
        <span className="text-lg font-bold text-brand">{big}</span><br />
        <span className="text-sm text-muted">{small}</span><br />
        <span className="text-muted text-sm">
            {plan.monthly > 0 ? 'No VAT payable' : <>&nbsp;</>}
        </span>
    </div>
}

const SubscriptionPeriod = ({billing, setBilling}) => {
    const opt = (title, value) => {
        return <span className={classNames(
                'mx-1 py-2 px-3 inline-block cursor rounded-lg bg-slate-50 border',
                value === billing ? 'font-semibold text-brand border-brand' : 'text-muted border-slate-200',
            )}
            onClick={() => setBilling(value)}
        >
            {title}
        </span>
    }
    return <div className="my-2">
        <div className="flex flex-row-reverse leading-none">
            {opt('Monthly', 'Monthly')}
            {opt('Annually', 'Yearly')}
            <span className="my-2 inline-block text-muted mr-2">
                Billed:
            </span>
        </div>
    </div>
}

export default Plan;
